import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AccionesMenu } from "@/components/shell/AccionesMenu";
import { Seccion, useSecciones } from "@/components/shell/Seccion";
import { TarjetaPeriodo, TarjetaTotal } from "@/components/shell/TarjetasPeriodo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer } from "lucide-react";
import {
  calcularCat,
  fmtMXN,
  fmtPct,
  SISTEMAS,
  type CatParams,
  type Sistema,
} from "@/lib/amortizacion";
import { validar } from "./ParamsPanel";

const DEFAULTS: CatParams = {
  monto: 50000,
  tasaMensual: 3.3,
  cuotas: 12,
  sistema: "frances",
  aperturaActiva: true,
  aperturaPct: 2,
  aperturaIva: true,
  adminActiva: true,
  adminMonto: 150,
  adminIva: true,
  seguroActivo: false,
  seguroPct: 0,
};

export function CalculadoraCat() {
  const [p, setP] = useState<CatParams>(DEFAULTS);
  const set = (patch: Partial<CatParams>) => setP({ ...p, ...patch });
  const errores = validar(p);
  const valido = Object.keys(errores).length === 0;
  const secs = useSecciones(["condiciones", "cargos"] as const);
  const res = useMemo(() => (valido ? calcularCat(p) : null), [p, valido]);

  const fechaHoy = new Intl.DateTimeFormat("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
          Calculadora de CAT
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Costo Anual Total conforme a la Circular 21/2009 de Banco de México. Se resuelve la tasa
          periódica i* por bisección numérica y se anualiza: CAT = ((1 + i*)^12 − 1) × 100.
        </p>
      </div>

      <div className="space-y-3">
        <Seccion
          titulo="Condiciones del crédito"
          abierta={secs.abierta("condiciones")}
          onToggle={() => secs.toggle("condiciones")}
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="cat-monto">Monto (MXN)</Label>
              <Input
                id="cat-monto"
                type="number"
                inputMode="decimal"
                className="num"
                value={Number.isNaN(p.monto) ? "" : p.monto}
                onChange={(e) => set({ monto: e.target.valueAsNumber })}
              />
              {errores.monto && <p className="text-xs text-destructive">{errores.monto}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-tasa">Tasa mensual (%)</Label>
              <Input
                id="cat-tasa"
                type="number"
                inputMode="decimal"
                step={0.01}
                className="num"
                value={Number.isNaN(p.tasaMensual) ? "" : p.tasaMensual}
                onChange={(e) => set({ tasaMensual: e.target.valueAsNumber })}
              />
              {errores.tasa && <p className="text-xs text-destructive">{errores.tasa}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cat-plazo">Plazo (meses)</Label>
              <Input
                id="cat-plazo"
                type="number"
                inputMode="decimal"
                min={1}
                max={120}
                className="num"
                value={Number.isNaN(p.cuotas) ? "" : p.cuotas}
                onChange={(e) => set({ cuotas: e.target.valueAsNumber })}
              />
              {errores.cuotas && <p className="text-xs text-destructive">{errores.cuotas}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Sistema de amortización</Label>
              <Select value={p.sistema} onValueChange={(v) => set({ sistema: v as Sistema })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SISTEMAS.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Seccion>

        <Seccion
          titulo="Comisiones y accesorios"
          abierta={secs.abierta("cargos")}
          onToggle={() => secs.toggle("cargos")}
        >
          <div className="space-y-4">
            <div className="grid gap-4 rounded-lg bg-surface p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="ap">Comisión por apertura (% sobre monto)</Label>
                <Input
                  id="ap"
                  type="number"
                  inputMode="decimal"
                  step={0.1}
                  className="num"
                  disabled={!p.aperturaActiva}
                  value={Number.isNaN(p.aperturaPct) ? "" : p.aperturaPct}
                  onChange={(e) => set({ aperturaPct: e.target.valueAsNumber })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ap-on"
                  checked={p.aperturaActiva}
                  onCheckedChange={(v) => set({ aperturaActiva: v })}
                />
                <Label htmlFor="ap-on">Activar</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ap-iva"
                  checked={p.aperturaIva}
                  disabled={!p.aperturaActiva}
                  onCheckedChange={(v) => set({ aperturaIva: v })}
                />
                <Label htmlFor="ap-iva">IVA 16%</Label>
              </div>
            </div>

            <div className="grid gap-4 rounded-lg bg-surface p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="ad">Comisión de administración (MXN por mes)</Label>
                <Input
                  id="ad"
                  type="number"
                  inputMode="decimal"
                  step={10}
                  className="num"
                  disabled={!p.adminActiva}
                  value={Number.isNaN(p.adminMonto) ? "" : p.adminMonto}
                  onChange={(e) => set({ adminMonto: e.target.valueAsNumber })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ad-on"
                  checked={p.adminActiva}
                  onCheckedChange={(v) => set({ adminActiva: v })}
                />
                <Label htmlFor="ad-on">Activar</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="ad-iva"
                  checked={p.adminIva}
                  disabled={!p.adminActiva}
                  onCheckedChange={(v) => set({ adminIva: v })}
                />
                <Label htmlFor="ad-iva">IVA 16%</Label>
              </div>
            </div>

            <div className="grid gap-4 rounded-lg bg-surface p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="sg">Prima de seguro (% sobre saldo insoluto por mes)</Label>
                <Input
                  id="sg"
                  type="number"
                  inputMode="decimal"
                  step={0.05}
                  className="num"
                  disabled={!p.seguroActivo}
                  value={Number.isNaN(p.seguroPct) ? "" : p.seguroPct}
                  onChange={(e) => set({ seguroPct: e.target.valueAsNumber })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="sg-on"
                  checked={p.seguroActivo}
                  onCheckedChange={(v) => set({ seguroActivo: v })}
                />
                <Label htmlFor="sg-on">Activar</Label>
              </div>
              <span />
            </div>
          </div>
        </Seccion>
      </div>

      {!res ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Corrige los parámetros para calcular el CAT.
        </p>
      ) : (
        <>
          <Card className="print-block border-navy bg-navy p-6 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-widest text-primary-foreground/70">
              Costo Anual Total (CAT)
            </p>
            <p className="mt-2 num text-5xl font-bold text-primary-foreground">
              {fmtPct(res.cat, 1)}
            </p>
            <p className="mt-3 max-w-2xl text-sm text-primary-foreground/80">
              CAT promedio: {fmtPct(res.cat, 1)}, sin IVA, calculado al {fechaHoy}. Para fines
              informativos y de comparación.
            </p>
            <p className="mt-2 num text-xs text-primary-foreground/60">
              Tasa periódica i* = {(res.tasaPeriodica * 100).toFixed(6)}% mensual
            </p>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { l: "Comisión de apertura con IVA", v: fmtMXN(res.comisionAperturaConIva) },
              { l: "Monto neto recibido", v: fmtMXN(res.montoNeto) },
              { l: "Comisión de administración con IVA", v: fmtMXN(res.adminConIva) },
              { l: "Cuota base", v: fmtMXN(res.cuotaBase) },
              { l: "Pago total mensual (período 1)", v: fmtMXN(res.pagoTotalMensual) },
              { l: "Total de intereses", v: fmtMXN(res.tabla.totalIntereses) },
            ].map((it) => (
              <Card key={it.l} className="print-block border-navy/15 p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {it.l}
                </p>
                <p className="mt-2 num text-xl font-semibold text-navy">{it.v}</p>
              </Card>
            ))}
          </div>

          <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b bg-surface px-5 py-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-navy">
                Flujo de pagos del cliente
              </h3>
              <AccionesMenu
                acciones={[
                  {
                    label: "Imprimir / PDF",
                    icono: <Printer className="h-4 w-4" />,
                    variant: "secondary",
                    onClick: () => window.print(),
                  },
                ]}
              />
            </div>
            <div className="solo-movil space-y-3 p-4 lg:hidden">
              {res.tabla.periodos.map((per, idx) => {
                const seguro = p.seguroActivo ? (per.saldoInicial * p.seguroPct) / 100 : 0;
                return (
                  <TarjetaPeriodo
                    key={per.periodo}
                    titulo={`Período ${per.periodo}`}
                    destacado={{ l: "Flujo total", v: fmtMXN(res.flujos[idx] ?? 0) }}
                    resumen={[{ l: "Cuota base", v: fmtMXN(per.cuota) }]}
                    detalle={[
                      { l: "Administración c/IVA", v: fmtMXN(res.adminConIva), tono: "muted" },
                      { l: "Seguro", v: fmtMXN(seguro), tono: "muted" },
                      { l: "Flujo total del período", v: fmtMXN(res.flujos[idx] ?? 0) },
                    ]}
                  />
                );
              })}
              <TarjetaTotal
                datos={[
                  { l: "Total intereses", v: fmtMXN(res.tabla.totalIntereses), tono: "chart3" },
                  { l: "Total pagado (cuotas)", v: fmtMXN(res.tabla.totalPagado) },
                  { l: "CAT", v: fmtPct(res.cat, 1), tono: "emerald" },
                ]}
              />
            </div>
            <div className="solo-desktop hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="bg-navy text-primary-foreground">
                    <th className="px-3 py-2.5 text-left font-medium">Período</th>
                    <th className="px-3 py-2.5 text-right font-medium">Cuota base</th>
                    <th className="px-3 py-2.5 text-right font-medium">Administración</th>
                    <th className="px-3 py-2.5 text-right font-medium">Seguro</th>
                    <th className="px-3 py-2.5 text-right font-medium">Flujo total</th>
                  </tr>
                </thead>
                <tbody>
                  {res.tabla.periodos.map((per, idx) => {
                    const seguro = p.seguroActivo ? (per.saldoInicial * p.seguroPct) / 100 : 0;
                    return (
                      <tr key={per.periodo} className="border-b last:border-0 odd:bg-surface/60">
                        <td className="px-3 py-2 num">{per.periodo}</td>
                        <td className="px-3 py-2 text-right num">{fmtMXN(per.cuota)}</td>
                        <td className="px-3 py-2 text-right num">{fmtMXN(res.adminConIva)}</td>
                        <td className="px-3 py-2 text-right num">{fmtMXN(seguro)}</td>
                        <td className="px-3 py-2 text-right num font-semibold">
                          {fmtMXN(res.flujos[idx] ?? 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
